/**
 * Сравнение двух хозяйств на каноне — перевод ответов в оси экрана.
 *
 * Источники два, и у каждого своя правда:
 *
 * * **`/season/picture?summary=true` → `summary.by_farm[]`** — оси ЦИКЛА:
 *   обязательства хозяйства (назначено / отчитались / проверено), «без
 *   отчёта», «просрочено», «расхождение», контуры и площадь в цикле. Строки
 *   нет — хозяйству в этом цикле ничего не назначено; это НЕ ноль по осям;
 * * **`/actors/{id}`** — земля хозяйства по реестру: контуры, общая и пашня,
 *   направления. Это знаменатель земли, а не цикла, и с осями цикла он не
 *   смешивается.
 *
 * Чего здесь нет намеренно: балла, рейтинга и итоговой оценки. Доли считаются
 * от знаменателя СВОЕГО хозяйства, никогда от района и никогда друг от друга.
 */

import type { Schemas } from '../../../api/client';
import { cropName } from '../../../lib/crops';
import { defineMessages } from '../../../i18n/define';
import { messageRecord } from '../../lib/messageRecord';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  official: {
    id: 'data.epistemic.officialData',
    defaultMessage: 'официальные данные',
    description: 'Класс величины словами в сравнении: данные из официальных источников (строчная фраза)',
  },
  derived: {
    id: 'data.epistemic.derivedShort',
    defaultMessage: 'производное',
    description: 'Класс величины словами в сравнении: вычислено из источников (строчная фраза)',
  },
  syntheticDemo: {
    id: 'data.epistemic.syntheticDemoCompare',
    defaultMessage: 'синтетика демонстрации',
    description: 'Класс величины словами в сравнении: демонстрационные данные, не факт (строчная фраза)',
  },
  hypothesis: {
    id: 'data.epistemic.hypothesisCompare',
    defaultMessage: 'гипотеза',
    description: 'Класс величины словами в сравнении: предположение, не факт (строчная фраза)',
  },
  noData: {
    id: 'data.noData',
    defaultMessage: 'нет данных',
    description: 'Вместо числа, когда величины нет (честное «нет данных», не ноль); коротко',
  },
});


export type BreakdownRow = Schemas['SeasonBreakdownRowOut'];
export type MeasuredOut = Schemas['MeasuredOut'];
type CycleRow = Schemas['SeasonCycleOut'];

/** Подписи класса величины; синтетика называется словами, а не цветом. */
export const EPISTEMIC_LABEL: Record<string, string> = messageRecord({
  official: m.official,
  derived: m.derived,
  synthetic_demo: m.syntheticDemo,
  hypothesis: m.hypothesis,
});

/** Величина для строки экрана: число или прямое «нет данных». */
export interface MeasuredView {
  /** null — величины нет, и это не ноль. */
  value: number | null;
  /** Подпись класса, если она нужна рядом с числом (синтетика — всегда). */
  note: string | null;
  synthetic: boolean;
}

/** «нет данных» на языке интерфейса. */
export function noData(): string {
  return tr(m.noData);
}

export function measuredView(m: MeasuredOut | null | undefined): MeasuredView {
  if (!m || m.value === null || m.value === undefined) {
    return { value: null, note: null, synthetic: false };
  }
  const synthetic = m.epistemic === 'synthetic_demo';
  return {
    value: m.value,
    note: synthetic ? EPISTEMIC_LABEL.synthetic_demo! : null,
    synthetic,
  };
}

/** Доля в процентах от СВОЕГО знаменателя; ноль в знаменателе — доли нет. */
export function shareOf(part: number | null, whole: number | null): number | null {
  if (part === null || whole === null || !whole) return null;
  return (part / whole) * 100;
}

/** Микрогектары → гектары; неизвестная площадь не равна нулю. */
export function microhaToHa(microha: number | null | undefined): number | null {
  if (microha === null || microha === undefined) return null;
  return microha / 1_000_000;
}

/** Оси цикла одного хозяйства. */
export interface CycleAxes {
  obligations: { total: number; reported: number; verified: number };
  /** Класс величины обязательств (синтетика — подписью). */
  obligationsNote: string | null;
  noReport: MeasuredView;
  overdue: MeasuredView;
  conflicts: MeasuredView;
  contours: number;
  areaHa: number | null;
}

export function cycleAxes(row: BreakdownRow): CycleAxes {
  const o = row.totals.obligations;
  return {
    obligations: { total: o.total, reported: o.reported, verified: o.verified },
    obligationsNote: measuredView(o.measured).note,
    noReport: measuredView(row.totals.no_report),
    overdue: measuredView(row.totals.overdue),
    conflicts: measuredView(row.totals.conflicts),
    contours: row.contours.total,
    areaHa: microhaToHa(row.area.total_microha),
  };
}

/** Земля хозяйства по реестру — из `/actors/{id}`. */
export interface LandFacts {
  /** null — имени у субъекта в реестре нет. */
  name: string | null;
  contours: number;
  totalHa: number | null;
  arableHa: number | null;
  directions: string[];
}

interface ActorLike {
  name: string | null;
  contours: number;
  total_microha: number | null;
  arable_microha: number | null;
  directions?: ReadonlyArray<{ direction_code: string; raw_label?: string | null }> | null;
}

export function landFacts(actor: ActorLike): LandFacts {
  // Направления приходят строкой на каждый код; один сырой ярлык («пахта-ғалла»)
  // может дать два кода — показываем коды без повторов, в порядке сервера.
  const seen = new Set<string>();
  const directions: string[] = [];
  for (const d of actor.directions ?? []) {
    if (seen.has(d.direction_code)) continue;
    seen.add(d.direction_code);
    directions.push(d.direction_code);
  }
  return {
    name: actor.name,
    contours: actor.contours,
    totalHa: microhaToHa(actor.total_microha),
    arableHa: microhaToHa(actor.arable_microha),
    directions,
  };
}

/**
 * Цикл по умолчанию: идущий (`active`), иначе первый в порядке сервера.
 * Осенью идут два цикла сразу (сбор хлопка и сев пшеницы) — тогда хлопок.
 * Выбор, сделанный человеком (адрес), важнее и проверяется до вызова.
 */
export function defaultCycle(items: ReadonlyArray<CycleRow>): string | null {
  return (
    items.find((c) => c.status === 'active' && c.crop_code === 'COTTON') ??
    items.find((c) => c.status === 'active') ??
    items[0]
  )?.cycle ?? null;
}

/**
 * Подпись цикла: культура и сезон — «Хлопок-2026». Одна на все экраны
 * (обзор района, хозяйства, карточка): русское имя культуры по коду, а не
 * словарное `crop_name` («Пахта»).
 */
export function cycleLabel(row: Pick<CycleRow, 'crop_code' | 'season_label'> & { crop_name?: string | null }): string {
  return `${cropName(row.crop_code, row.crop_name)}-${row.season_label}`;
}

/**
 * Прошлый цикл той же культуры и дата «та же дата прошлого года» для
 * сравнения «к прошлому сезону на ту же дату». Считается на клиенте из
 * списка циклов и даты среза.
 */
export function previousCycle<T extends Pick<CycleRow, 'crop_code' | 'season_label'>>(items: ReadonlyArray<T>, cycle: T | null): T | null {
  if (!cycle) return null;
  const prevLabel = String(Number(cycle.season_label) - 1);
  return items.find((c) => c.crop_code === cycle.crop_code && c.season_label === prevLabel) ?? null;
}

/** Та же календарная дата годом раньше (29.02 → 28.02). */
export function sameDateLastYear(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number) as [number, number, number];
  const last = new Date(Date.UTC(y - 1, m, 0)).getUTCDate();
  return `${y - 1}-${String(m).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

/** Поиск хозяйства по имени среди строк цикла; порядок сервера сохраняется. */
export function matchFarms(
  rows: ReadonlyArray<BreakdownRow>,
  query: string,
  limit: number,
): BreakdownRow[] {
  const q = query.trim().toLowerCase();
  const pool = q ? rows.filter((r) => (r.name ?? '').toLowerCase().includes(q)) : rows;
  return pool.slice(0, limit);
}

/** Разбор `?a=` / `?b=`: только положительное целое, иначе выбора нет. */
export function parseFarmId(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return id > 0 ? id : null;
}

/* ------------------------------------------------ культуры и вода хозяйства */

/** Культура сезона на контурах хозяйства — строка размещений канона. */
export interface CropShare {
  name: string;
  contours: number;
  areaHa: number;
}

export interface CropFacts {
  season: string;
  /** По убыванию площади. */
  crops: CropShare[];
  /** Контуры хозяйства, которым размещение сезона не назначено. */
  unplacedContours: number;
  /** Их площадь; null — у кого-то из них площадь не названа. */
  unplacedHa: number | null;
  /** Размещения сезона — синтетика демонстрации. */
  demo: boolean;
}

interface ActorPlacementsLike {
  land_uses: ReadonlyArray<{ contour_id: number; total_microha: number | null }>;
  placement_details: ReadonlyArray<{
    season: string;
    is_demo: boolean;
    rows: ReadonlyArray<{ contour_id: number }>;
    by_crop: ReadonlyArray<{ crop_code?: string | null; crop_name: string; area_microha: number; contours: number }>;
  }>;
}

/**
 * Культуры хозяйства в сезоне из `placement_details` карточки `/actors/{id}`.
 * Блока сезона нет — `null`: размещений у хозяйства в каноне нет, и это не
 * «пустая земля». Контур без строки размещения назван числом, а не спрятан.
 */
export function cropFacts(actor: ActorPlacementsLike, season: string): CropFacts | null {
  const block = actor.placement_details.find((b) => b.season === season);
  if (!block) return null;
  const placed = new Set(block.rows.map((r) => r.contour_id));
  const unplaced = new Map<number, number | null>();
  for (const use of actor.land_uses) {
    if (placed.has(use.contour_id)) continue;
    const prev = unplaced.get(use.contour_id);
    const part = use.total_microha;
    unplaced.set(
      use.contour_id,
      prev === undefined ? part : prev === null || part === null ? null : prev + part,
    );
  }
  const areas = [...unplaced.values()];
  return {
    season,
    crops: block.by_crop
      .map((c) => ({ name: cropName(c.crop_code, c.crop_name), contours: c.contours, areaHa: c.area_microha / 1_000_000 }))
      .sort((a, b) => b.areaHa - a.areaHa),
    unplacedContours: unplaced.size,
    unplacedHa: areas.some((a) => a === null)
      ? null
      : areas.reduce<number>((s, a) => s + (a ?? 0), 0) / 1_000_000,
    demo: block.is_demo,
  };
}

/** Вода хозяйства в цикле — строка `summary.water_by_actor`. */
export interface WaterFacts {
  /** Строк заявок хозяйства. */
  lines: number;
  requestedM3: number | null;
  deliveredM3: number | null;
}

interface WaterActorLike {
  actor_id: number;
  lines: number;
  requested_volume_m3: string | null;
  delivered_volume_m3: string | null;
}

function volume(raw: string | null): number | null {
  if (raw === null || raw === '') return null;
  const v = Number(raw);
  return Number.isFinite(v) ? v : null;
}

/** Строки нет — `null`: в журнале воды цикла хозяйства нет. */
export function waterFacts(rows: ReadonlyArray<WaterActorLike>, id: number): WaterFacts | null {
  const row = rows.find((r) => r.actor_id === id);
  if (!row) return null;
  return {
    lines: row.lines,
    requestedM3: volume(row.requested_volume_m3),
    deliveredM3: volume(row.delivered_volume_m3),
  };
}
